import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PollIcon from '@mui/icons-material/Poll';
import { useLanguage, useCommonTranslation, useBillingTranslation } from '@/i18n/hooks/useTypedTranslation';
import { CreditBalanceWidget } from '@/components/billing/CreditBalanceWidget';
import PaymentIcon from '@mui/icons-material/Payment';
import logo from '@/assets/images/logo.png';
import { useModules, useIsModuleEnabled } from '@/modules/ModuleContext';

export function Header() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { t } = useCommonTranslation();
  const { t: tBilling } = useBillingTranslation();
  useModules();
  const isSurveyEnabled = useIsModuleEnabled('survey');
  const isExamEnabled = useIsModuleEnabled('exam');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);
  // const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState<null | HTMLElement>(null); // Workspace dropdown removed
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCreateMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCreateMenuAnchor(event.currentTarget);
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchor(null);
  };

  const handleCreateItem = (path: string) => {
    handleCreateMenuClose();
    navigate(path);
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const handleAdminMenuItem = (path: string) => {
    handleAdminMenuClose();
    navigate(path);
  };

  // Workspace dropdown handlers removed
  // const handleWorkspaceMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
  //   setWorkspaceMenuAnchor(event.currentTarget);
  // };

  // const handleWorkspaceMenuClose = () => {
  //   setWorkspaceMenuAnchor(null);
  // };

  // const handleWorkspaceMenuItem = (path: string) => {
  //   handleWorkspaceMenuClose();
  //   navigate(path);
  // };

  const createMenuItems = [
    { path: '/surveys/new', label: t('navigation.newSurvey'), icon: PollIcon, color: '#2BC4AD', moduleCode: 'survey', enabled: isSurveyEnabled },
    { path: '/exams/new', label: t('navigation.newExam'), icon: SchoolIcon, color: '#1F2A44', moduleCode: 'exam', enabled: isExamEnabled },
  ];

  // Workspace dropdown removed - using direct nav links instead
  // const workspaceMenuItems = [
  //   { path: '/exams', label: t('navigation.exams'), icon: SchoolIcon },
  //   { path: '/quizzes', label: t('navigation.quizzes'), icon: QuizIcon },
  //   { path: '/assessments', label: t('navigation.assessments'), icon: AssignmentIcon },
  // ];

  const adminMenuItems = [
    { path: '/admin/billing', label: 'Billing', icon: PaymentIcon },
    { path: '/admin', label: t('navigation.admin'), icon: AdminPanelSettingsIcon },
  ];

  const handleLogout = async () => {
    handleMenuClose();
    try {
      await logout();
    } catch (error) {
      // Even if API call fails, we still want to navigate to login
      console.error('Logout error:', error);
    }
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navLinks = [
    { href: '/dashboard', label: t('navigation.dashboard'), icon: DashboardIcon, moduleCode: null }, // Dashboard always available
    { href: '/surveys', label: t('navigation.surveys'), icon: PollIcon, moduleCode: 'survey', enabled: isSurveyEnabled },
    { href: '/exams', label: t('navigation.exams'), icon: SchoolIcon, moduleCode: 'exam', enabled: isExamEnabled },
  ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: alpha('#fff', 0.8),
        backdropFilter: 'blur(20px)',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Toolbar
        sx={{
          maxWidth: 1280,
          width: '100%',
          mx: 'auto',
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Logo & Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexGrow: 1 }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/dashboard"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="NLPask"
              sx={{
                height: { xs: 32, sm: 38 },
                width: 'auto',
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isModuleDisabled = !!(link.moduleCode && link.enabled === false);
              const isLinkActive = isActive(link.href);

              return (
                <Box key={link.href} sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <Button
                    component={isModuleDisabled ? 'button' : Link}
                    to={isModuleDisabled ? undefined : link.href}
                    startIcon={<Icon sx={{ fontSize: 18 }} />}
                    disabled={isModuleDisabled}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      color: isLinkActive ? 'primary.main' : 'text.secondary',
                      bgcolor: isLinkActive ? alpha('#1F2A44', 0.08) : 'transparent',
                      fontWeight: 500,
                      opacity: isModuleDisabled ? 0.6 : 1,
                      cursor: isModuleDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': {
                        bgcolor: isModuleDisabled ? 'transparent' : (isLinkActive ? alpha('#1F2A44', 0.12) : alpha('#000', 0.04)),
                      },
                      '&.Mui-disabled': {
                        color: 'text.secondary',
                        opacity: 0.6,
                      },
                    }}
                  >
                    {link.label}
                    {isModuleDisabled && (
                      <Chip
                        label="Coming Soon"
                        size="small"
                        sx={{
                          ml: 1,
                          height: 18,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: alpha('#FFA500', 0.15),
                          color: '#FF8C00',
                          '& .MuiChip-label': {
                            px: 0.75,
                          },
                        }}
                      />
                    )}
                  </Button>
                </Box>
              );
            })}

            {/* Workspace Dropdown - removed (using direct nav links) */}
            {/* <Button
              startIcon={<AssignmentIcon sx={{ fontSize: 18 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
              onClick={handleWorkspaceMenuOpen}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                color: (location.pathname.startsWith('/exams') ||
                        location.pathname.startsWith('/quizzes') ||
                        location.pathname.startsWith('/assessments'))
                  ? 'primary.main'
                  : 'text.secondary',
                bgcolor: (location.pathname.startsWith('/exams') ||
                          location.pathname.startsWith('/quizzes') ||
                          location.pathname.startsWith('/assessments'))
                  ? alpha('#14B8A6', 0.1)
                  : 'transparent',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: (location.pathname.startsWith('/exams') ||
                            location.pathname.startsWith('/quizzes') ||
                            location.pathname.startsWith('/assessments'))
                    ? alpha('#14B8A6', 0.15)
                    : alpha('#000', 0.04),
                },
              }}
            >
              Workspace
            </Button> */}

            {/* Admin Dropdown - Only for Admins */}
            {isAdmin && (
              <Button
                startIcon={<AdminPanelSettingsIcon sx={{ fontSize: 18 }} />}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                onClick={handleAdminMenuOpen}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  color: (location.pathname.startsWith('/admin')) ? 'primary.main' : 'text.secondary',
                  bgcolor: (location.pathname.startsWith('/admin')) ? alpha('#1F2A44', 0.08) : 'transparent',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: (location.pathname.startsWith('/admin')) ? alpha('#1F2A44', 0.12) : alpha('#000', 0.04),
                  },
                }}
              >
                Admin
              </Button>
            )}
          </Box>
        </Box>

        {/* Right Side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Credit Balance Widget - Desktop */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <CreditBalanceWidget compact={true} />
          </Box>

          {/* Quick Create Button - Desktop with Dropdown */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleCreateMenuOpen}
            sx={{
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            {t('navigation.create')}
          </Button>

          {/* Quick Create Button - Mobile Icon */}
          <IconButton
            color="primary"
            onClick={handleCreateMenuOpen}
            sx={{
              display: { xs: 'flex', sm: 'none' },
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <AddIcon />
          </IconButton>

          {/* Create Menu Dropdown */}
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={handleCreateMenuClose}
            disableScrollLock
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            slotProps={{
              paper: {
                sx: { width: 240, mt: 1 },
              },
            }}
          >
            {createMenuItems.map((item) => {
              const Icon = item.icon;
              const isDisabled = item.enabled === false;

              return (
                <MenuItem
                  key={item.path}
                  onClick={() => !isDisabled && handleCreateItem(item.path)}
                  disabled={isDisabled}
                  sx={{
                    py: 1.5,
                    opacity: isDisabled ? 0.6 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ListItemIcon>
                    <Icon fontSize="small" sx={{ color: item.color }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.label}
                      {isDisabled && (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 600,
                            bgcolor: alpha('#FFA500', 0.15),
                            color: '#FF8C00',
                            '& .MuiChip-label': {
                              px: 0.75,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </ListItemText>
                </MenuItem>
              );
            })}
          </Menu>

          {/* Workspace Menu Dropdown - removed (using direct nav links) */}
          {/* <Menu
            anchorEl={workspaceMenuAnchor}
            open={Boolean(workspaceMenuAnchor)}
            onClose={handleWorkspaceMenuClose}
            disableScrollLock
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            slotProps={{
              paper: {
                sx: { width: 200, mt: 1 },
              },
            }}
          >
            {workspaceMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <MenuItem
                  key={item.path}
                  onClick={() => handleWorkspaceMenuItem(item.path)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              );
            })}
          </Menu> */}

          {/* Admin Menu Dropdown */}
          {isAdmin && (
            <Menu
              anchorEl={adminMenuAnchor}
              open={Boolean(adminMenuAnchor)}
              onClose={handleAdminMenuClose}
              disableScrollLock
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              slotProps={{
                paper: {
                  sx: { width: 200, mt: 1 },
                },
              }}
            >
              {adminMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <MenuItem
                    key={item.path}
                    onClick={() => handleAdminMenuItem(item.path)}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{item.label}</ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>
          )}

          {/* User Menu - Desktop */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              display: { xs: 'none', md: 'flex' },
              borderRadius: 3,
              px: 1,
              py: 0.5,
              '&:hover': {
                bgcolor: alpha('#000', 0.04),
              },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: '#1F2A44',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {user?.fullName || 'User'}
              </Typography>
              {isAdmin && (
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, fontSize: 10 }}>
                  Administrator
                </Typography>
              )}
            </Box>
            <KeyboardArrowDownIcon sx={{ color: 'text.secondary', ml: 0.5, display: { xs: 'none', sm: 'block' } }} />
          </IconButton>

          {/* Mobile Menu Button */}
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            slotProps={{
              paper: {
                sx: { width: 280, mt: 1 },
              },
            }}
          >
            {/* User Info */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                    {user?.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user?.email}
                  </Typography>
                </Box>
              </Box>
              {isAdmin && (
                <Chip
                  label={t('header.administrator')}
                  size="small"
                  color="primary"
                  sx={{ mt: 1, height: 22, fontSize: 11 }}
                />
              )}
            </Box>

            {/* Menu Items */}
            <Box sx={{ py: 1 }}>
              <MenuItem
                component={Link}
                to="/dashboard"
                onClick={handleMenuClose}
                sx={{ borderRadius: 1, mx: 1 }}
              >
                <ListItemIcon>
                  <DashboardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('navigation.dashboard')}</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/settings"
                onClick={handleMenuClose}
                sx={{ borderRadius: 1, mx: 1 }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('navigation.settings')}</ListItemText>
              </MenuItem>
              <MenuItem
                component={Link}
                to="/billing"
                onClick={handleMenuClose}
                sx={{ borderRadius: 1, mx: 1 }}
              >
                <ListItemIcon>
                  <PaymentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{tBilling('navigation.billing')}</ListItemText>
              </MenuItem>
            </Box>

            <Divider />

            {/* Language Selection */}
            <Box sx={{ py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t('language.select')}
              </Typography>
              {supportedLanguages.map((lang) => (
                <MenuItem
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    handleMenuClose();
                  }}
                  sx={{ borderRadius: 1, mx: 1 }}
                >
                  <ListItemIcon>
                    <LanguageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{lang.nativeName}</ListItemText>
                  {lang.code === currentLanguage && (
                    <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  )}
                </MenuItem>
              ))}
            </Box>

            <Divider />

            <Box sx={{ py: 1 }}>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  color: 'error.main',
                  '&:hover': {
                    bgcolor: alpha('#ef4444', 0.1),
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t('navigation.signOut')}</ListItemText>
              </MenuItem>
            </Box>
          </Menu>
        </Box>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        disableScrollLock
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280 },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer Header */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: '#1F2A44',
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleDrawerToggle} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          <List sx={{ flex: 1, py: 1 }}>
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isModuleDisabled = !!(link.moduleCode && link.enabled === false);
              const isLinkActive = isActive(link.href);

              return (
                <ListItem key={link.href} disablePadding>
                  <ListItemButton
                    component={isModuleDisabled ? 'div' : Link}
                    to={isModuleDisabled ? undefined : link.href}
                    onClick={isModuleDisabled ? undefined : handleDrawerToggle}
                    disabled={isModuleDisabled}
                    sx={{
                      py: 1.5,
                      px: 2,
                      color: isLinkActive ? 'primary.main' : 'text.primary',
                      bgcolor: isLinkActive ? alpha('#1F2A44', 0.08) : 'transparent',
                      opacity: isModuleDisabled ? 0.6 : 1,
                      cursor: isModuleDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon sx={{ color: isLinkActive ? 'primary.main' : 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {link.label}
                        {isModuleDisabled && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 9,
                              fontWeight: 600,
                              bgcolor: alpha('#FFA500', 0.15),
                              color: '#FF8C00',
                              '& .MuiChip-label': {
                                px: 0.75,
                              },
                            }}
                          />
                        )}
                      </Box>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>
              );
            })}

            {/* Workspace Section in Mobile - removed (using direct nav links) */}
            {/* <ListItem sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Workspace
              </Typography>
            </ListItem>
            {workspaceMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    onClick={handleDrawerToggle}
                    sx={{
                      py: 1.5,
                      px: 2,
                      color: isActive(item.path) ? 'primary.main' : 'text.primary',
                      bgcolor: isActive(item.path) ? alpha('#14B8A6', 0.1) : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              );
            })} */}

            {/* Admin Section in Mobile */}
            {isAdmin && (
              <>
                <ListItem sx={{ px: 2, py: 1, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                    Admin
                  </Typography>
                </ListItem>
                {adminMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <ListItem key={item.path} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        sx={{
                          py: 1.5,
                          px: 2,
                          color: isActive(item.path) ? 'primary.main' : 'text.primary',
                          bgcolor: isActive(item.path) ? alpha('#14B8A6', 0.1) : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Icon sx={{ color: isActive(item.path) ? 'primary.main' : 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </>
            )}

            <ListItem disablePadding sx={{ mt: 1 }}>
              <ListItemButton
                component={Link}
                to="/settings"
                onClick={handleDrawerToggle}
                sx={{ py: 1.5, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsIcon sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText primary={t('navigation.settings')} />
              </ListItemButton>
            </ListItem>
          </List>

          {/* Credit Balance - Mobile */}
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <CreditBalanceWidget compact={false} showBreakdown={false} />
          </Box>

          <Divider />

          {/* Language Selection */}
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('language.select')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {supportedLanguages.map((lang) => (
                <Button
                  key={lang.code}
                  variant={lang.code === currentLanguage ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    changeLanguage(lang.code);
                  }}
                  sx={{ flex: 1 }}
                >
                  {lang.nativeName}
                </Button>
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Logout */}
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={() => {
                handleDrawerToggle();
                handleLogout();
              }}
            >
              {t('navigation.signOut')}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default Header;
